"use client";

import { useEffect, useState } from "react";
import { AVATAR } from "@/lib/ui-classes";

interface Props {
  src?: string | null;
  name?: string | null;
  email?: string | null;
  size?: number; // px, default 32
  className?: string;
}

/**
 * Round avatar that falls back to an initial-letter circle when:
 *   - no src is provided, OR
 *   - the image fails to load (broken URL, removed Google avatar, etc.)
 *
 * The OAuth `picture_url` from Google/Facebook can be missing, expired,
 * or hotlink-restricted - using <img> directly leaves a broken icon.
 */
export default function Avatar({ src, name, email, size = 32, className = "" }: Props) {
  const [failed, setFailed] = useState(false);

  // Reset failed flag if src changes (e.g., user switches accounts)
  useEffect(() => { setFailed(false); }, [src]);

  const initial = ((name || email || "?").trim()[0] || "?").toUpperCase();
  const dim = { width: size, height: size };

  if (!src || failed) {
    return (
      <div
        style={dim}
        className={`rounded-full ${AVATAR} flex items-center justify-center font-bold ${className}`}
      >
        <span style={{ fontSize: Math.max(11, Math.round(size * 0.42)) }}>{initial}</span>
      </div>
    );
  }

  return (
    /* eslint-disable-next-line @next/next/no-img-element */
    <img
      src={src}
      alt=""
      style={dim}
      onError={() => setFailed(true)}
      referrerPolicy="no-referrer"
      className={`rounded-full border border-gray-200 object-cover ${className}`}
    />
  );
}
